import type { Call, Device } from "@twilio/voice-sdk";
import { EVENTS } from "~/modules/analytics/Analytics";
import type { VoiceCall } from "~/modules/VoiceCall";
import { VoiceCallImpl } from "~/modules/VoiceCall";
import { ErrorCode, ErrorSeverity, FlexSdkError } from "~/modules/error";
import { getLogger } from "~/modules/logger";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { VoiceControllerImpl } from "~/modules/voice/VoiceControllerImpl";
import { Action } from "~/modules/actions/Action";
import {
    addCallContextParameter,
    isSupervisorAlreadyMonitoringCallTask,
    sendTrackingEvent
} from "~/modules/actions/ActionUtils";
import { CbmImpl } from "~/modules/cbm/CbmImpl";
import { SessionImpl } from "~/modules/session/Session/SessionImpl";
import { AnalyticsImpl } from "~/modules/analytics/AnalyticsImpl";




export interface MonitorCallOptions {
    extraParams?: Record<any, any>;
    voiceDevice?: Device;
}





















export class MonitorCall implements Action<Promise<VoiceCall>> {
    


    #taskSid: string;

    


    #reservationSid: string;

    


    #options: MonitorCallOptions = {};

    constructor(taskSid: string, reservationSid: string, options: MonitorCallOptions = {}) {
        this.#taskSid = taskSid;
        this.#reservationSid = reservationSid;
        this.#options = options;
    }

    async run(ctx: {}): Promise<VoiceCall> {
        const context = ctx as ContextManager;

        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();

        const session = context.retrieveInstanceOf(ContextInstance.SessionImpl, SessionImpl);
        const flexSessionIdentifier = session.flexSessionIdentifier;

        const voiceController = context.retrieveInstanceOf(ContextInstance.VoiceControllerImpl, VoiceControllerImpl);
        const logger = getLogger(context)("MonitorCall");
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);

        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        await cbm.init();

        logger.debug(`monitorCall invoked with taskSid: ${this.#taskSid}`);

        const worker = taskRouter.worker;
        const task = await taskRouter.workspace.fetchTask(this.#taskSid);

        if (!worker) {
            const errorMsg = "monitorCall: worker is not initialized";
            logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        if (await isSupervisorAlreadyMonitoringCallTask(task, worker, cbm)) {
            const errorMsg = `monitorCall: supervisor is already monitoring call for taskSid: ${this.#taskSid}`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.MonitorCallCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                reservationSid: this.#reservationSid
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        const effectiveExtraParams = {
            ...this.#options.extraParams,
            To: addCallContextParameter(
                `flex_session_identifier=${flexSessionIdentifier}`,
                taskRouter.worker.attributes.contact_uri,
                this.#options.extraParams?.To
            )
        };

        const voiceDevice = this.#options.voiceDevice;
        await voiceController.init(voiceDevice);

        return new Promise((resolve, reject) => {
            const handleIncomingCall = (call: Call) => {
                logger.info("Incoming call event received in monitorCall");

                const voiceCall = new VoiceCallImpl(context, call, voiceController.voiceDevice);

                sendTrackingEvent(EVENTS.MonitorCallCompleted, analytics, worker, {
                    taskSid: this.#taskSid,
                    reservationSid: this.#reservationSid,
                    callSid: voiceCall.call?.parameters?.CallSid
                });

                voiceController.unsubscribeFromIncomingCallEvent(handleIncomingCall);

                resolve(voiceCall);
            };
            voiceController.subscribeToIncomingCallEvent(handleIncomingCall);

            taskRouter.worker.monitor(this.#taskSid, this.#reservationSid, effectiveExtraParams).catch((error) => {
                const errorMsg = `monitorCall: Failed to monitor call: ${error.message}`;
                logger.error(errorMsg);
                sendTrackingEvent(EVENTS.MonitorCallCompleted, analytics, worker, {
                    error: errorMsg,
                    taskSid: this.#taskSid,
                    reservationSid: this.#reservationSid
                });
                return reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
            });
        });
    }
}
